import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Briefcase, Plus, Trash2, MapPin, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function Jobs() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", company: "", location: "", description: "" });
  const [expanded, setExpanded] = useState(null);

  const { data: jobs = [], isLoading } = useQuery({ queryKey: ["jobs"], queryFn: async () => (await api.get("/jobs")).data });

  const createMut = useMutation({
    mutationFn: () => api.post("/jobs", form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      setOpen(false);
      setForm({ title: "", company: "", location: "", description: "" });
      toast.success("Puesto creado");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/jobs/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["jobs"] }); toast.success("Puesto eliminado"); },
  });

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Puestos</h1>
          <p className="text-slate-400 mt-1.5">Job descriptions contra las que se evalúa a los candidatos.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-job-btn" className="gap-2 bg-primary text-slate-950 hover:bg-cyan-400">
              <Plus size={18} /> Nuevo puesto
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Nuevo puesto</DialogTitle>
              <DialogDescription className="text-slate-400">Pega la job description completa. Se usará para evaluar el encaje de los candidatos.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-slate-300">Título del puesto</Label>
                <Input data-testid="job-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 bg-slate-950 border-slate-800 focus:border-primary" placeholder="Ej. Senior Data Streaming Engineer" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300">Empresa</Label>
                  <Input data-testid="job-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5 bg-slate-950 border-slate-800 focus:border-primary" />
                </div>
                <div>
                  <Label className="text-slate-300">Ubicación</Label>
                  <Input data-testid="job-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5 bg-slate-950 border-slate-800 focus:border-primary" />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Descripción / requisitos</Label>
                <Textarea data-testid="job-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={8} className="mt-1.5 bg-slate-950 border-slate-800 focus:border-primary" placeholder="Pega aquí la job description completa: responsabilidades, requisitos esenciales y deseables…" />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="save-job-btn" disabled={createMut.isPending || !form.title.trim() || !form.description.trim()} onClick={() => createMut.mutate()} className="bg-primary text-slate-950 hover:bg-cyan-400">
                {createMut.isPending ? "Guardando…" : "Crear puesto"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="text-slate-500">Cargando…</div>
      ) : jobs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/40 p-12 text-center text-slate-500">
          <Briefcase className="mx-auto mb-3 text-slate-600" size={36} strokeWidth={1.5} />
          Aún no hay puestos. Crea una job description para empezar.
        </div>
      ) : (
        <div className="grid gap-3">
          {jobs.map((j, i) => (
            <motion.div key={j.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="rounded-xl border border-slate-800 bg-slate-900 p-5" data-testid={`job-card-${j.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="font-heading text-lg font-medium tracking-tight text-slate-100">{j.title}</h3>
                  <div className="flex flex-wrap gap-4 mt-1 text-xs text-slate-500">
                    {j.company && <span className="flex items-center gap-1"><Building2 size={12} /> {j.company}</span>}
                    {j.location && <span className="flex items-center gap-1"><MapPin size={12} /> {j.location}</span>}
                  </div>
                </div>
                <button data-testid={`delete-job-${j.id}`} onClick={() => deleteMut.mutate(j.id)} className="text-slate-500 hover:text-red-400 transition-colors shrink-0">
                  <Trash2 size={16} />
                </button>
              </div>
              <p className={`text-sm text-slate-400 mt-3 whitespace-pre-wrap ${expanded === j.id ? "" : "line-clamp-3"}`}>{j.description}</p>
              <button onClick={() => setExpanded(expanded === j.id ? null : j.id)} className="text-xs text-primary hover:underline mt-2">
                {expanded === j.id ? "Ver menos" : "Ver descripción completa"}
              </button>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
